<section
  class="economy-cards"
  [class.economy-cards--fill]="fillHeight"
  aria-label="Cenário de economia do cliente">
  @if (isLoading()) {
    <div class="economy-cards__grid" aria-busy="true" aria-live="polite">
      @for (item of [0, 1]; track item) {
        <div class="economy-cards__card economy-cards__card--loading">
          <app-cog-loading-spinner [size]="28"></app-cog-loading-spinner>
          <div class="economy-cards__body">
            <span class="economy-cards__skeleton economy-cards__skeleton--label"></span>
            <span class="economy-cards__skeleton economy-cards__skeleton--value"></span>
            <span class="economy-cards__skeleton economy-cards__skeleton--sub"></span>
          </div>
        </div>
      }
    </div>
  } @else if (hasError()) {
    <div class="economy-cards__error" role="alert">
      <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
      <span
        >Não foi possível carregar a economia prevista. Use Tentar de novo para buscar outra
        vez.</span
      >
      <app-button
        label="Tentar de novo"
        theme="white"
        size="small"
        [noMargin]="true"
        [fitContent]="true"
        (sendButtonClick)="retry()"></app-button>
    </div>
  } @else {
    @if (data()?.status === 'no-benchmark') {
      <div class="economy-cards__pending economy-cards__pending--no-benchmark">
        <i class="pi pi-sliders-h"></i>
        <span>Defina o benchmark da empresa para ver a economia vs mercado.</span>
      </div>
    } @else if (data()?.status === 'pending') {
      <div class="economy-cards__pending">
        <i class="pi pi-clock"></i>
        <span>Cenário sendo calculado… Os dados estarão disponíveis em breve.</span>
      </div>
    } @else {
      <!-- Faixa do período aplicado aos cards (card 3323) -->
      @if (isLoadingPeriod() || periodData() || periodError()) {
        <div class="economy-cards__period-banner" aria-live="polite">
          <i class="pi pi-calendar" aria-hidden="true"></i>
          @if (isLoadingPeriod()) {
            <span>
              Calculando a economia na data passada ({{ periodProgress().done + 1 }} de
              {{ periodProgress().total }})… pode levar alguns segundos por empresa.
            </span>
          } @else if (periodData(); as view) {
            <span>
              Cards em <strong>{{ view.period.to | vaultDate }}</strong> · período aplicado
              @if (view.period.from) {
                ({{ view.period.from | vaultDate }} a {{ view.period.to | vaultDate }})
              }
              — só contam os derivativos já contratados nessa data.
              @if (view.delta && view.period.from) {
                <span class="economy-cards__period-delta">
                  No período: Prevista Inicial R$&nbsp;{{
                    (view.delta.projected | vaultFormatCurrency: 2) || '—'
                  }}
                  · realizado R$&nbsp;{{ (view.delta.actual | vaultFormatCurrency: 2) || '—' }} ·
                  contratual R$&nbsp;{{ (view.delta.contractual | vaultFormatCurrency: 2) || '—' }}
                </span>
              }
              @if (simulationData()) {
                A data passada usa o benchmark da empresa, não a simulação.
              }
              @if (view.partial) {
                <span class="economy-cards__period-delta">
                  Alguma operação não pôde ser calculada nessa data e ficou fora da soma. Aplique o
                  período de novo em alguns minutos.
                </span>
              }
            </span>
          } @else {
            <span>
              Não foi possível calcular a economia na data escolhida. Os cards continuam em hoje;
              tente aplicar o período de novo.
            </span>
          }
          @if (!isLoadingPeriod()) {
            <app-button
              label="Voltar para hoje"
              theme="link"
              size="small"
              [noMargin]="true"
              [fitContent]="true"
              (sendButtonClick)="resetPeriod()"></app-button>
          }
        </div>
      }
      <!-- Faixa de simulação: aparece quando o benchmark do gráfico é aplicado aos cards -->
      @if (simulationData() || isSimulating() || simulationError()) {
        <div class="economy-cards__sim-banner" aria-live="polite">
          <i class="pi pi-sliders-h" aria-hidden="true"></i>
          @if (isSimulating()) {
            <span>Simulando economia contra o benchmark selecionado no gráfico…</span>
          } @else if (simulationData()) {
            <span>
              Simulação · vs <strong>{{ simulationData()?.benchmarkLabel }}</strong> — valores
              exploratórios, não substituem o benchmark oficial.
            </span>
            <button
              type="button"
              class="economy-cards__sim-reset"
              (click)="resetSimulation()"
              aria-label="Voltar ao benchmark oficial">
              Voltar ao benchmark oficial
            </button>
          } @else {
            <span>Não foi possível simular — exibindo o benchmark oficial da empresa.</span>
          }
        </div>
      }
      <div class="economy-cards__grid">
        <!-- Economia PREVISTA — primeiro (esquerda) -->
        <div class="economy-cards__card economy-cards__card--indigo">
          <span class="economy-cards__icon economy-cards__icon--indigo">
            <i class="pi pi-chart-bar"></i>
          </span>
          <div class="economy-cards__body">
            <div class="economy-cards__label economy-cards__label--row">
              @if (periodData(); as view) {
                <span>Economia Prevista Inicial em {{ view.period.to | vaultDate }}</span>
              } @else {
                <span>Economia Prevista Inicial</span>
              }
              <i
                class="pi pi-info-circle economy-cards__info"
                pTooltip="Projeção contratual da economia até o vencimento de todas as operações elegíveis, comparada ao benchmark da empresa. Base: fluxo completo contratado (mesma régua do Fluxo Comparativo)."
                tooltipPosition="top"></i>
            </div>
            @if (isLoadingPeriod() || (isSimulating() && !periodData())) {
              <span class="economy-cards__skeleton economy-cards__skeleton--value"></span>
            } @else if (periodData(); as view) {
              <div class="economy-cards__value">
                R$&nbsp;{{ (view.end.projectedSavingsAsOf | vaultFormatCurrency: 2) || '—' }}
              </div>
            } @else {
              <div class="economy-cards__value">
                R$&nbsp;{{
                  (simulationData()
                    ? simulationData()?.economiaPrevistaInicial
                    : data()?.economiaPrevista
                  ) | vaultFormatCurrency: 2
                }}
              </div>
            }
            <div class="economy-cards__sub">
              @if (periodData(); as view) {
                Projeção contratual até o vencimento · derivativos contratados até
                {{ view.period.to | vaultDate }}
                @if (view.delta && view.period.from) {
                  <span
                    class="economy-cards__period-change"
                    [class.economy-cards__period-change--up]="
                      view.delta.projected !== null && view.delta.projected >= 0
                    "
                    [class.economy-cards__period-change--down]="
                      view.delta.projected !== null && view.delta.projected < 0
                    ">
                    No período ({{ view.period.from | vaultDate }} a
                    {{ view.period.to | vaultDate }}): R$&nbsp;{{
                      (view.delta.projected | vaultFormatCurrency: 2) || '—'
                    }}
                  </span>
                }
              } @else if (simulationData()) {
                Projeção contratual até o vencimento · Simulação vs
                {{ simulationData()?.benchmarkLabel }}
              } @else {
                Projeção contratual até o vencimento ·
                <span class="economy-cards__keep">vs {{ data()?.benchmarkLabel || '—' }}</span>
              }
            </div>
            <div class="economy-cards__footer-row">
              @if (data()?.activeLoanCount && !simulationData() && !isSimulating()) {
                <!-- Período aplicado: contagem da data final e sem abrir a lista de hoje (card 3323) -->
                <button
                  type="button"
                  class="economy-cards__ops"
                  [class.economy-cards__ops--clickable]="hasPerOperation && !periodData()"
                  [disabled]="!hasPerOperation || !!periodData()"
                  [attr.aria-label]="hasPerOperation ? 'Ver operações da economia prevista' : null"
                  (click)="openOperationsModal()">
                  @if (periodData(); as view) {
                    {{ view.end.activeLoanCount }} operações elegíveis em
                    {{ view.period.to | vaultDate }}
                  } @else {
                    {{ data()?.activeLoanCount }} operações elegíveis
                  }
                  @if (hasPerOperation && !periodData()) {
                    <i class="pi pi-arrow-right economy-cards__ops-arrow"></i>
                  }
                </button>
              }
            </div>
          </div>
        </div>

        <!-- Economia REAL — segundo (direita) -->
        <div class="economy-cards__card economy-cards__card--green">
          <span class="economy-cards__icon economy-cards__icon--green">
            <i class="pi pi-check-circle"></i>
          </span>
          <div class="economy-cards__body">
            <div class="economy-cards__label economy-cards__label--row">
              @if (periodData(); as view) {
                <span>Economia realizada até {{ view.period.to | vaultDate }}</span>
              } @else {
                <span>Economia Prevista até Final</span>
              }
              <i
                class="pi pi-info-circle economy-cards__info"
                pTooltip="Projeção atualizada da economia até o vencimento: parcelas passadas com valores realizados (fluxo de caixa, incluindo hedge e variação cambial) e futuras projetadas. Pode diferir da Inicial conforme o realizado — ex.: variação cambial favorável."
                tooltipPosition="top"></i>
            </div>
            @if (isLoadingPeriod()) {
              <span class="economy-cards__skeleton economy-cards__skeleton--value"></span>
            } @else if (periodData(); as view) {
              <div class="economy-cards__value">
                R$&nbsp;{{ (view.end.actualSavingsToDate | vaultFormatCurrency: 2) || '—' }}
              </div>
            } @else if (isSimulating()) {
              <span class="economy-cards__skeleton economy-cards__skeleton--value"></span>
            } @else {
              <div class="economy-cards__value">
                R$&nbsp;{{
                  (simulationData()
                    ? simulationData()?.economiaPrevistaAtual
                    : data()?.economiaReal
                  ) | vaultFormatCurrency: 2
                }}
              </div>
            }
            <div class="economy-cards__sub">
              @if (periodData(); as view) {
                Contratual até {{ view.period.to | vaultDate }}: R$&nbsp;{{
                  (view.end.contractualSavingsToDate | vaultFormatCurrency: 2) || '—'
                }}
              } @else if (simulationData()) {
                Projeção atualizada até o vencimento · Simulação vs
                {{ simulationData()?.benchmarkLabel }}
              } @else {
                Projeção atualizada até o vencimento ·
                <span class="economy-cards__keep">vs {{ data()?.benchmarkLabel || '—' }}</span>
              }
            </div>
            <div class="economy-cards__footer-row">
              @if (data()?.activeLoanCount && !simulationData() && !isSimulating()) {
                <!-- Período aplicado: contagem da data final e sem abrir a lista de hoje (card 3323) -->
                <button
                  type="button"
                  class="economy-cards__ops"
                  [class.economy-cards__ops--clickable]="hasPerOperation && !periodData()"
                  [disabled]="!hasPerOperation || !!periodData()"
                  [attr.aria-label]="hasPerOperation ? 'Ver operações da economia real' : null"
                  (click)="openOperationsModal()">
                  @if (periodData(); as view) {
                    {{ view.end.activeLoanCount }} operações elegíveis em
                    {{ view.period.to | vaultDate }}
                  } @else {
                    {{ data()?.activeLoanCount }} operações elegíveis
                  }
                  @if (hasPerOperation && !periodData()) {
                    <i class="pi pi-arrow-right economy-cards__ops-arrow"></i>
                  }
                </button>
              }
              @if (simulationData()?.hasPartialFailures) {
                <span class="economy-cards__badge economy-cards__badge--partial">Parcial</span>
              } @else if (data()?.status === 'partial' && !simulationData()) {
                <span class="economy-cards__badge economy-cards__badge--partial">Parcial</span>
              }
            </div>
          </div>
        </div>
      </div>
    }
  }
</section>

<app-economy-operations-modal #operationsModal></app-economy-operations-modal>
